import { IconLineFile, IconLineImage, IconLineTrash, IconLineVideo, IconLineVisible } from "@kalodata/kds-design-icons";
import { Progress } from "antd";
import classNames from "classnames";
import { Trans } from "react-i18next";

export interface ClipUploadFileCardProps {
  name: string;
  type?: string;
  url?: string;
  thumbUrl?: string;
  status: "uploading" | "done" | "error";
  percent?: number;
  width?: number | string;
  height?: number | string;
  showProgress?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  onRemove?: () => void;
  /** 触发预览的回调；不传则隐藏预览按钮 */
  onPreview?: () => void;
}

const isImageType = (type?: string): boolean => !!type && type.startsWith("image/");
const isVideoType = (type?: string): boolean => !!type && type.startsWith("video/");

const FileTypeIcon = ({ type }: { type?: string }) => {
  if (isImageType(type)) return <IconLineImage size={32} className="text-kds-fg-subtle" />;
  if (isVideoType(type)) return <IconLineVideo size={32} className="text-kds-fg-subtle" />;
  return <IconLineFile size={32} className="text-kds-fg-subtle" />;
};

const FileThumbnail = ({
  name,
  type,
  url,
  thumbUrl,
}: {
  name: string;
  type?: string;
  url?: string;
  thumbUrl?: string;
}) => {
  if (!url && !thumbUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-kds-bg-subtle">
        <FileTypeIcon type={type} />
      </div>
    );
  }
  if (isImageType(type)) {
    return <img src={thumbUrl ?? url} alt={name} className="h-full w-full object-cover" />;
  }
  if (isVideoType(type)) {
    if (!url && thumbUrl) {
      return <img src={thumbUrl} alt={name} className="h-full w-full object-cover" />;
    }
    return (
      <video src={url} poster={thumbUrl} className="h-full w-full object-cover" preload="metadata" muted playsInline />
    );
  }
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-[6px] bg-kds-bg-subtle p-[8px]">
      <FileTypeIcon type={type} />
      <span className="line-clamp-2 break-all text-center text-[12px] leading-[1.3] text-kds-fg-soft">{name}</span>
    </div>
  );
};

interface ActionButtonProps {
  ariaLabel: string;
  onClick: () => void;
  children: React.ReactNode;
}

const ActionButton = ({ ariaLabel, onClick, children }: ActionButtonProps) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className="flex size-[28px] cursor-pointer items-center justify-center rounded-full text-kds-light transition-colors hover:bg-kds-light/15"
  >
    {children}
  </button>
);

export const ClipUploadFileCard = ({
  name,
  type,
  url,
  thumbUrl,
  status,
  percent = 0,
  width,
  height,
  showProgress = true,
  disabled = false,
  errorMessage,
  onRemove,
  onPreview,
}: ClipUploadFileCardProps) => {
  const isUploading = status === "uploading";
  const isError = status === "error";
  const isDone = status === "done";

  const previewHandler = !disabled && isDone ? onPreview : undefined;
  const removeHandler = !disabled && !isUploading ? onRemove : undefined;
  const showActionMask = !!previewHandler || !!removeHandler;

  return (
    <div
      className={classNames(
        "group relative shrink-0 overflow-hidden rounded-[8px] border bg-kds-bg-element",
        isError ? "border-kds-danger-border" : "border-kds-border-subtle",
      )}
      style={{ width, height }}
    >
      <div className="absolute inset-0">
        <FileThumbnail name={name} type={type} url={url} thumbUrl={thumbUrl} />
      </div>

      {isUploading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-[8px] bg-kds-mask/60 px-[12px]">
          {showProgress ? (
            <Progress
              type="line"
              percent={Math.round(percent)}
              showInfo={false}
              strokeColor="var(--kds-accent)"
              railColor="var(--kds-bg-overlay)"
              className="mb-0! w-full"
            />
          ) : null}
          <span className="text-[12px] leading-[1.4] text-kds-light">
            <Trans>Uploading...</Trans>
            {showProgress ? ` ${Math.round(percent)}%` : null}
          </span>
        </div>
      )}

      {isError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-[4px] bg-kds-mask/60 px-[12px] text-center">
          <span className="text-[12px] font-medium leading-[1.4] text-kds-light">
            <Trans>Upload failed</Trans>
          </span>
          {errorMessage ? (
            <span className="line-clamp-2 break-all text-[11px] leading-[1.4] text-kds-light/80">{errorMessage}</span>
          ) : null}
        </div>
      )}

      {showActionMask && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-[12px] bg-kds-mask/60 opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100">
          {previewHandler ? (
            <ActionButton ariaLabel="preview" onClick={previewHandler}>
              <IconLineVisible size={18} />
            </ActionButton>
          ) : null}
          {removeHandler ? (
            <ActionButton ariaLabel="remove" onClick={removeHandler}>
              <IconLineTrash size={18} />
            </ActionButton>
          ) : null}
        </div>
      )}
    </div>
  );
};
